import { createAsyncThunk, createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import authAxios from 'utils/authAxios';

// Thunks
export const fetchAdminOrders = createAsyncThunk(
  'adminOrder/fetchAdminOrders',
  async (params = {}) => {
    return authAxios.get('/admin-api/orders/', { params }).then(response => response.data);
  }
);

export const fetchOrderCopyOrders = createAsyncThunk(
  'adminOrder/fetchOrderCopyOrders',
  async (orderId) => {
    return authAxios.get(`/admin-api/orders/${orderId}/copy_orders/`)
      .then(response => ({
        orderId,
        data: response.data
      }));
  }
);

// Create entity adapter
export const adminOrdersAdapter = createEntityAdapter({
  // Using the 'id' field as the key
  selectId: (order) => order.id
});

// Initial state with loading, error, entities, and filter
const initialState = adminOrdersAdapter.getInitialState({
  loading: false,
  error: null,
  copyOrders: {},
  copyOrdersLoading: false,
  copyOrdersError: null,
  currentExpandedOrder: null,
  filter: {
    orderId: null,
    strategyId: null,
    tradeId: null,
    date: null,
    orderStatus: null,
    active: null
  }
});

// Create the slice
export const adminOrderSlice = createSlice({
  name: 'adminOrder',
  initialState,
  reducers: {
    setOrderFilter: (state, action) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    clearOrderFilter: (state) => {
      state.filter = initialState.filter;
    },
    setCurrentExpandedOrder: (state, action) => {
      state.currentExpandedOrder = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchAdminOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminOrders.fulfilled, (state, action) => {
        adminOrdersAdapter.setAll(state, action.payload);
        state.loading = false;
      })
      .addCase(fetchAdminOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      
      // Fetch copy orders
      .addCase(fetchOrderCopyOrders.pending, (state) => {
        state.copyOrdersLoading = true;
        state.copyOrdersError = null;
      })
      .addCase(fetchOrderCopyOrders.fulfilled, (state, action) => {
        const { orderId, data } = action.payload;
        state.copyOrders[orderId] = data;
        state.copyOrdersLoading = false;
      })
      .addCase(fetchOrderCopyOrders.rejected, (state, action) => {
        state.copyOrdersLoading = false;
        state.copyOrdersError = action.error.message;
      });
  }
});

// Export actions
export const { 
  setOrderFilter, 
  clearOrderFilter,
  setCurrentExpandedOrder
} = adminOrderSlice.actions;

// Export selectors
export const {
  selectAll: selectAllAdminOrders,
  selectById: selectAdminOrderById,
  selectIds: selectAdminOrderIds
} = adminOrdersAdapter.getSelectors((state) => state.adminOrder);

// Custom selectors
export const selectOrderFilter = (state) => state.adminOrder.filter;
export const selectCurrentExpandedOrder = (state) => state.adminOrder.currentExpandedOrder;
export const selectCopyOrders = (state, orderId) => state.adminOrder.copyOrders[orderId] || [];
export const selectCopyOrdersLoading = (state) => state.adminOrder.copyOrdersLoading;

// Selector to get orders organized hierarchically (parent orders at top level)
export const selectOrdersHierarchy = createSelector(
  selectAllAdminOrders,
  (orders) => {
    // Find all parent orders (orders without a parent)
    const parentOrders = orders.filter(order => !order.parent_order_id);
    
    // Function to find children for a parent order
    const findChildOrders = (parentId) => {
      return orders.filter(order => order.parent_order_id === parentId);
    };
    
    // Add child_orders property to each parent order
    return parentOrders.map(parent => ({
      ...parent,
      child_orders: findChildOrders(parent.id)
    }));
  }
);

export default adminOrderSlice.reducer; 